import ActivityInput from "@/pages/(app)/returns/view/components/return-activity/activity-input";
import ActivityList from "@/pages/(app)/returns/view/components/return-activity/activity-list";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import { useGetDebugLogs } from "@babackapp/private-api-client/client/use-get-debug-logs";
import useTranslation from "@babackapp/translations/use-translation";
import { Label } from "@babackapp/ui/components/label";
import { Switch } from "@babackapp/ui/components/switch";
import { ActivityDeactivateAutoTreatLogistician } from "@/pages/(app)/returns/view/components/return-activity/activity-deactivate-auto-treat-logistician.tsx";
import { useEffect, useRef, useState } from "react";

type ReturnRequestActivityProps = {
  returnRequest: ReturnRequestDto;
  markAsRead: () => void;
};

export default function Activity({
  returnRequest,
  markAsRead,
}: ReturnRequestActivityProps) {
  const t = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isShowDebugLogs, setIsShowDebugLogs] = useState(false);

  const { data: debugLogs } = useGetDebugLogs({
    shopId: returnRequest.shopId,
    returnRequestId: returnRequest.id,
    enabled: isShowDebugLogs,
  });

  useEffect(() => {
    if (!ref.current) return;
    // Observe the activity list specifically, not the wrapper that includes the
    // input — otherwise scrolling just the input into view would mark notes as read.
    const target =
      ref.current.querySelector<HTMLElement>("#return-request-activities") ??
      ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) markAsRead();
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [markAsRead]);

  return (
    <div className="mt-6" ref={ref}>
      <div className="flex items-center justify-between mb-4">
        <Label className="flex-1">{t("returnDetail.activity")}</Label>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Switch
              id="debug-logs-toggle"
              checked={isShowDebugLogs}
              onCheckedChange={setIsShowDebugLogs}
              className="scale-75"
            />
            <Label
              htmlFor="debug-logs-toggle"
              className="text-xs text-muted-foreground font-normal cursor-pointer"
            >
              Logs techniques
            </Label>
          </div>
          <ActivityDeactivateAutoTreatLogistician
            returnRequest={returnRequest}
          />
        </div>
      </div>
      <div className={"mt-2 flex flex-col gap-2"}>
        <ActivityInput
          returnRequestId={returnRequest.id}
          shopId={returnRequest.shopId}
        />
        <ActivityList
          activities={returnRequest.logs}
          debugLogs={isShowDebugLogs ? debugLogs : undefined}
          isShowTechnicalDetails={isShowDebugLogs}
          returnRequestId={returnRequest.id}
        />
      </div>
    </div>
  );
}
